import {
  Box,
  Button,
  Checkbox,
  Grid,
  OutlinedInput,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import DateInput from "../../../components/DateInput";
import { DATE_MODE, DATE_STR_START, MONTH_STR_END } from "../../../components/DateInput";
import { NormalTable } from "../../../components/TableCustomize";
import { getReceivableDailyCheck } from "../../../http/api/BalanceAPI";
import { getHyphenedDate } from "../../../utils/utils";
import { CompanyTitledPage } from "../../TitledPage";
import { accountBalanceColumn, accountBalanceMonthlyColumn, totalBalanceColumn, totalBalanceMonthlyColumn } from "./enhance";

const AccountSearchArea = ({
  onSearchCondition,
  searchConditionInput,
  changeSearchConditionInput,
}) => {
  const onFocusInput = (event) => {
    event.target.select();
  };

  const onClickSearch = (event) => {
    onSearchCondition();
  };
  const handleChangeSearchCondition = (e) => {
    changeSearchConditionInput(e);
  };

  return (
    <Box border="1px solid blue" borderRadius={1} padding={2}>
      検索
      <Box margin={2} marginBottom={1}>
        <Grid container alignItems="end" spacing={2}>
          <Grid item xs>
            買人(No/氏名):&nbsp;
            <OutlinedInput
              onFocus={onFocusInput}
              onChange={({ target: { value } }) =>
                handleChangeSearchCondition({ keyWord: value })
              }
              value={searchConditionInput.keyWord}
              sx={{ width: 250 }}
            />
            <Stack
              direction="row"
              alignItems="center"
              flexWrap="wrap"
              marginTop={2}
            >
              <Stack direction="row" alignItems="center">
                <Checkbox
                  checked={searchConditionInput.filterByToday}
                  onChange={(_e, v) =>
                    handleChangeSearchCondition({ filterByToday: v })
                  }
                />
                <Typography>本日売上ありで絞る</Typography>
              </Stack>
              <Stack direction="row" alignItems="center">
                <Checkbox
                  checked={searchConditionInput.paymentToday}
                  onChange={(_e, v) =>
                    handleChangeSearchCondition({ paymentToday: v })
                  }
                />
                <Typography>本日入金ありで絞る</Typography>
              </Stack>
              <Stack direction="row" alignItems="center">
                <Checkbox
                  checked={searchConditionInput.balanceToday}
                  onChange={(_e, v) => {
                    const condition = v ? { negativeBalanceToday: false } : {};
                    handleChangeSearchCondition({
                      balanceToday: v,
                      ...condition,
                    });
                  }}
                />

                <Typography>本日残高プラスで絞る</Typography>
              </Stack>
              <Stack direction="row" alignItems="center">
                <Checkbox
                  checked={searchConditionInput.negativeBalanceToday}
                  onChange={(_e, v) => {
                    const condition = v ? { balanceToday: false } : {};
                    handleChangeSearchCondition({
                      negativeBalanceToday: v,
                      ...condition,
                    });
                  }}
                />

                <Typography>本日残高マイナスで絞る</Typography>
              </Stack>
            </Stack>
          </Grid>

          <Grid item xs="auto">
            <Button variant="contained" onClick={onClickSearch}>
              検索
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default function AccountReceivableBalance() {
  // どのメニューから遷移したかに応じて表示切替用のモードを設定する
  const location = useLocation();
  const pathMode = location?.pathname.split("/")[2];
  const mode = (pathMode === "account_receivable_balance") ? DATE_MODE.DATE : DATE_MODE.MONTH;

  const [totalBalanceData, setTotalBalanceData] = useState([]);
  const [accountBalanceData, setAccountBalanceData] = useState([]);

  const [shippingDateInput, setShippingDateInput] = useState(
    getHyphenedDate(new Date())
  );
  const [searchConditionInput, setSearchConditionInput] = useState({
    keyWord: "",
    filterByToday: false,
    paymentToday: false,
    balanceToday: false,
    negativeBalanceToday: false,
  });

  // 画面を日計表と月次残高一覧で切り替えた際の初期化処理
  useEffect(() => {
    setShippingDateInput((mode === DATE_MODE.DATE) ? getHyphenedDate(new Date()) : getHyphenedDate(new Date()).substring(DATE_STR_START, MONTH_STR_END));
    setSearchConditionInput({
      keyWord: "",
      filterByToday: false,
      paymentToday: false,
      balanceToday: false,
      negativeBalanceToday: false,
    });
  }, [mode]);

  const [state, setState] = useState({
    pagination: { page: 0, pageSize: 25, totalPage: 1 },
  });

  const { pagination } = state;

  const handleChangePageProps = (page) => {
    setState({ ...state, pagination: { ...pagination, page } });
  };

  const changeSearchConditionInput = (e) => {
    setSearchConditionInput({
      ...searchConditionInput,
      ...e,
    });
  };
  const updateShippingDate = (shippingDate) => {
    setShippingDateInput(shippingDate);
  };
  const onSearchCondition = () => {
    getData({ page: 0 });
  };

  const getData = async (data = {}) => {
    const date = (mode === DATE_MODE.DATE ? shippingDateInput : shippingDateInput.substring(DATE_STR_START, MONTH_STR_END) + '-01');
    const params = {
      mode: mode.ID,
      date: date,
      ...searchConditionInput,
      page: pagination.page,
      size: pagination.pageSize,
      ...data,
    };
    const result = await getReceivableDailyCheck(params);
    if (result.data) {
      const {
        total,
        data: { items, page, pageSize, totalPage },
      } = result.data;
      setTotalBalanceData(total);
      setAccountBalanceData(items);
      setState({
        ...state,
        pagination: { ...pagination, pageSize, page, totalPage },
      });
    }
  };

  // ページ/タブ/対象日および対象年月切替時の再検索処理
  useEffect(() => {
    getData();
  }, [pagination.page, shippingDateInput]);

  return (
    <CompanyTitledPage  title={mode === DATE_MODE.DATE ? "売掛金日計表確認" : "売掛金残高一覧"} hideCurrentTime>
      <Grid container spacing={2} alignItems="center" marginBottom={2}>
        <Grid item xs>
          <Typography sx={{ fontSize: 20, textAlign: "right" }}>
            {mode === DATE_MODE.DATE ? "日付 :" : "年月："}
          </Typography>
        </Grid>
        <Grid item xs="auto">
          <DateInput
            value={mode === DATE_MODE.DATE ? shippingDateInput : shippingDateInput.substring(DATE_STR_START, MONTH_STR_END)}
            setValue={updateShippingDate}
            maxDate={getHyphenedDate(new Date())}
            mode={mode}
          />
        </Grid>
      </Grid>
      <AccountSearchArea
        onSearchCondition={onSearchCondition}
        searchConditionInput={searchConditionInput}
        changeSearchConditionInput={changeSearchConditionInput}
      />
      <NormalTable columns={mode === DATE_MODE.DATE ? totalBalanceColumn : totalBalanceMonthlyColumn} data={totalBalanceData} hover />
      <NormalTable
        columns={mode === DATE_MODE.DATE ? accountBalanceColumn : accountBalanceMonthlyColumn}
        data={accountBalanceData}
        pagination={{ ...pagination, handleChangePageProps }}
        paginationTop
      />
    </CompanyTitledPage>
  );
}
